import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, HardHat, Truck, UserX, Wrench, Trash2 } from "lucide-react";
import { useApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Loading, NativeSelect, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import Timeline, { Legend, siteColor } from "../components/Timeline";
import { GoogleCalendarButton } from "../components/Integrations";
import { useAssignmentDelete } from "../components/AssignmentDelete";
import { EditTimeButton, useAssignmentEdit } from "../components/AssignmentEdit";
import { addDays, fmtDate, fmtLong, parseISO, toISO, todayISO, weekStart } from "../lib/format";
import { cn } from "../lib/utils";

const covers = (e, d) => (!e.start_date || e.start_date <= d) && (!e.end_date || e.end_date >= d);

function range(view, cursor) {
  if (view === "day") return [cursor, cursor];
  if (view === "week") return [weekStart(cursor), addDays(weekStart(cursor), 6)];
  const d = parseISO(cursor);
  const first = toISO(new Date(d.getFullYear(), d.getMonth(), 1));
  const last = toISO(new Date(d.getFullYear(), d.getMonth() + 1, 0));
  return [weekStart(first), addDays(weekStart(last), 6)];
}

function groupBySite(events) {
  const bySite = {};
  for (const x of events) {
    const g = bySite[x.site_id] || (bySite[x.site_id] = { name: x.site_name, w: 0, v: 0, conflict: false, id: x.site_id });
    if (x.worker_id) g.w += 1;
    else g.v += 1;
    if (x.kind === "conflict") g.conflict = true;
  }
  return Object.values(bySite);
}

function monthDays(cursor) {
  const [s, e] = range("month", cursor);
  const days = [];
  for (let d = s; d <= e; d = addDays(d, 1)) days.push(d);
  return days;
}

function MonthDayCell({ d, data, month, today, onPick }) {
  const sites = groupBySite(data.events.filter((x) => covers(x, d)));
  const abs = data.absences.filter((x) => covers(x, d)).length;
  const isToday = d === today;
  const outside = parseISO(d).getMonth() !== month;
  return (
    <button onClick={() => onPick(d)} className={cn("min-h-[92px] sm:min-h-[110px] rounded-lg border p-1.5 text-left align-top hover:border-amber-500/60 transition-colors",
      outside ? "border-border/30 opacity-40" : "border-border", isToday && "ring-1 ring-amber-500")} data-testid={`calendar-day-${d}`}>
      <div className={cn("text-xs num mb-1", isToday && "text-amber-400 font-bold")}>{parseISO(d).getDate()}</div>
      <div className="space-y-0.5">
        {sites.slice(0, 3).map((x) => (
          <div key={x.id} className={cn("rounded px-1 py-0.5 text-[10px] truncate text-white", x.conflict ? "bg-red-600" : siteColor(x.id))}>
            <span className="hidden sm:inline">{x.name} · </span>{x.w}👷 {x.v > 0 && `${x.v}🚚`}
          </div>
        ))}
        {sites.length > 3 && <div className="text-[10px] text-muted-foreground">+{sites.length - 3}</div>}
        {abs > 0 && <div className="rounded px-1 py-0.5 text-[10px] bg-orange-500 text-slate-950 truncate">{abs} assenti</div>}
      </div>
    </button>
  );
}

const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function MonthView({ cursor, data, onPick }) {
  const month = parseISO(cursor).getMonth();
  const today = todayISO();
  return (
    <div data-testid="calendar-month-view">
      <div className="grid grid-cols-7 text-xs text-muted-foreground mb-1">{WEEKDAYS.map((d) => <div key={d} className="p-2">{d}</div>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {monthDays(cursor).map((d) => <MonthDayCell key={d} d={d} data={data} month={month} today={today} onPick={onPick} />)}
      </div>
    </div>
  );
}

function DayItem({ x, onDelete, onEdit }) {
  return (
    <div className={cn("flex items-center gap-2 text-sm rounded-md px-2 py-1.5 bg-background/50", x.kind === "conflict" && "bg-red-950 text-red-200")} data-testid={`calendar-day-item-${x.id}`}>
      {x.worker_id ? <HardHat className="h-4 w-4 text-amber-400" /> : <Truck className="h-4 w-4 text-sky-400" />}
      <span className="truncate flex-1">{x.resource_name}</span><span className="text-xs text-muted-foreground num">{x.shift}</span>
      {onEdit && <EditTimeButton onClick={() => onEdit(x)} testid={`calendar-time-assignment-${x.id}`} />}
      {onDelete && <button onClick={() => onDelete(x)} className="text-red-400 hover:text-red-300 p-1" data-testid={`calendar-delete-assignment-${x.id}`} aria-label="Cancella assegnazione"><Trash2 className="h-3.5 w-3.5" /></button>}
    </div>
  );
}

function DayView({ day, data, onDelete, onEdit }) {
  const ev = data.events.filter((x) => covers(x, day));
  const bySite = {};
  ev.forEach((x) => { (bySite[x.site_id] = bySite[x.site_id] || { name: x.site_name, id: x.site_id, items: [] }).items.push(x); });
  const abs = data.absences.filter((x) => covers(x, day));
  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="calendar-day-view">
      <div className="lg:col-span-2 space-y-3">
        {!Object.keys(bySite).length && <Empty title="Nessuna assegnazione in questo giorno" />}
        {Object.values(bySite).map((s) => (
          <div key={s.id} className="rounded-lg border border-border overflow-hidden">
            <div className={cn("px-4 py-2 font-semibold text-white", siteColor(s.id))}>{s.name}</div>
            <div className="p-3 grid sm:grid-cols-2 gap-2">
              {s.items.map((x) => <DayItem key={x.id} x={x} onDelete={onDelete} onEdit={onEdit} />)}
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        <div className="rounded-lg border border-orange-600/60 p-3"><div className="eyebrow mb-2 flex items-center gap-1 text-orange-300"><UserX className="h-3.5 w-3.5" />Assenze</div>
          {!abs.length ? <div className="text-sm text-muted-foreground">Nessuna</div> : abs.map((a) => <div key={a.id} className="text-sm">{a.resource_name} <span className="text-muted-foreground">· {a.reason}</span></div>)}</div>
        {data.unavailable.length > 0 && <div className="rounded-lg border border-purple-600/60 p-3"><div className="eyebrow mb-2 flex items-center gap-1 text-purple-300"><Wrench className="h-3.5 w-3.5" />Indisponibili</div>
          {data.unavailable.map((a) => <div key={a.id} className="text-sm">{a.resource_name} <span className="text-muted-foreground">· {a.reason}</span></div>)}</div>}
      </div>
    </div>
  );
}

function calendarTitle(view, cursor, s, e) {
  if (view === "day") return fmtLong(cursor);
  if (view === "week") return `${fmtDate(s)} → ${fmtDate(e)}`;
  return parseISO(cursor).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
}

function CalendarBody({ view, cursor, start, data, loading, onPickDay, onDelete, onEdit }) {
  if (loading && !data) return <Loading />;
  if (view === "month") return <MonthView cursor={cursor} data={data} onPick={onPickDay} />;
  if (view === "day") return <DayView day={cursor} data={data} onDelete={onDelete} onEdit={onEdit} />;
  return <Timeline events={data.events} absences={data.absences} unavailable={data.unavailable} start={start} days={7} onDayClick={onPickDay} />;
}

function CalendarFilters({ sites, siteId, setSiteId, res, setRes }) {
  return (
    <>
      <NativeSelect value={siteId} onChange={(ev) => setSiteId(ev.target.value)} className="w-auto" data-testid="calendar-site-filter">
        <option value="">Tutti i cantieri</option>{sites?.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
      </NativeSelect>
      <NativeSelect value={res} onChange={(ev) => setRes(ev.target.value)} className="w-auto" data-testid="calendar-resource-filter">
        <option value="all">Operai e mezzi</option><option value="worker">Solo operai</option><option value="vehicle">Solo mezzi</option>
      </NativeSelect>
    </>
  );
}

function moveCursor(view, cursor, dir) {
  if (view === "day") return addDays(cursor, dir);
  if (view === "week") return addDays(cursor, dir * 7);
  const d = parseISO(cursor);
  return toISO(new Date(d.getFullYear(), d.getMonth() + dir, 1));
}

function useCalendar() {
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(todayISO());
  const [siteId, setSiteId] = useState("");
  const [res, setRes] = useState("all");
  const [s, e] = range(view, cursor);
  const { data, loading, reload } = useApi("/calendar", { start_date: s, end_date: e, site_id: siteId || undefined });
  const filtered = useMemo(() => {
    if (!data) return null;
    const keepW = res !== "vehicle";
    const keepV = res !== "worker";
    return { ...data, events: data.events.filter((x) => (x.worker_id ? keepW : keepV)), absences: keepW ? data.absences : [], unavailable: keepV ? data.unavailable : [] };
  }, [data, res]);
  const move = (dir) => setCursor(moveCursor(view, cursor, dir));
  const pickDay = (d) => {
    setCursor(d);
    setView("day");
  };
  return { view, setView, cursor, setCursor, siteId, setSiteId, res, setRes, s, e, data, filtered, loading, reload, move, pickDay };
}

function CalendarToolbar({ cal, staff }) {
  return (
    <div className="surface p-3 sm:p-4 mb-4 flex flex-col lg:flex-row gap-3 lg:items-center">
      <div className="flex items-center gap-1">
        <Button size="icon" variant="outline" onClick={() => cal.move(-1)} data-testid="calendar-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" onClick={() => cal.setCursor(todayISO())} data-testid="calendar-today-btn">Oggi</Button>
        <Button size="icon" variant="outline" onClick={() => cal.move(1)} data-testid="calendar-next-btn"><ChevronRight className="h-4 w-4" /></Button>
        <span className="ml-3 font-semibold capitalize" data-testid="calendar-title">{calendarTitle(cal.view, cal.cursor, cal.s, cal.e)}</span>
      </div>
      <div className="lg:ml-auto flex flex-wrap gap-2">
        <Tabs value={cal.view} onValueChange={cal.setView}>
          <TabsList>
            <TabsTrigger value="day" data-testid="calendar-view-day">Giorno</TabsTrigger>
            <TabsTrigger value="week" data-testid="calendar-view-week">Settimana</TabsTrigger>
            <TabsTrigger value="month" data-testid="calendar-view-month">Mese</TabsTrigger>
          </TabsList>
        </Tabs>
        {staff && <CalendarFilters sites={cal.data?.sites} siteId={cal.siteId} setSiteId={cal.setSiteId} res={cal.res} setRes={cal.setRes} />}
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const { user } = useAuth();
  const cal = useCalendar();
  const assignDelete = useAssignmentDelete(cal.reload);
  const timeEdit = useAssignmentEdit(cal.reload);
  const staff = user.role !== "operaio";
  return (
    <div>
      <PageHeader eyebrow="Pianificazione" title="Calendario" subtitle={staff ? "Cantieri, operai, mezzi e assegnazioni dal database" : "Il tuo calendario personale"}
        actions={<GoogleCalendarButton start={cal.s} end={cal.e} siteId={cal.siteId ? Number(cal.siteId) : null} />} />
      <CalendarToolbar cal={cal} staff={staff} />
      <div className="surface p-3 sm:p-4">
        <div className="mb-3"><Legend /></div>
        <CalendarBody view={cal.view} cursor={cal.cursor} start={cal.s} data={cal.filtered} loading={cal.loading} onPickDay={cal.pickDay}
          onDelete={staff ? assignDelete.ask : null} onEdit={staff ? timeEdit.ask : null} />
      </div>
      {assignDelete.dialog}
      {timeEdit.dialog}
    </div>
  );
}
